import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Free address autocomplete using Komoot's Photon (OpenStreetMap-based).
 * No API key required. Debounced 300ms.
 * Props: value, onChange (raw string), onSelect(selection { label, lat, lon }), placeholder, testId, className
 */
export function AddressAutocomplete({ value, onChange, onSelect, placeholder, testId, className }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = (q) => {
    if (!q || q.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController(); abortRef.current = ac;
    setLoading(true);
    fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=en`, { signal: ac.signal })
      .then((r) => r.json())
      .then((data) => {
        const feats = (data?.features || []).map((f) => {
          const p = f.properties || {};
          const line1 = [p.housenumber, p.street].filter(Boolean).join(" ") || p.name;
          const line2 = [p.city || p.town || p.village, p.state, p.postcode, p.country].filter(Boolean).join(", ");
          return {
            label: [line1, line2].filter(Boolean).join(", "),
            lon: f.geometry?.coordinates?.[0],
            lat: f.geometry?.coordinates?.[1],
            street: [p.housenumber, p.street].filter(Boolean).join(" ") || "",
            city: p.city || p.town || p.village || "",
            state: p.state || "",
            postcode: p.postcode || "",
            country: p.country || "",
          };
        }).filter((s) => s.label);
        setSuggestions(feats);
        setOpen(feats.length > 0);
        setHighlight(-1);
      })
      .catch(() => { /* aborted or offline */ })
      .finally(() => setLoading(false));
  };

  const onInput = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const choose = (s) => {
    onChange(s.label);
    onSelect?.(s);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); choose(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className || ""}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8A847A] pointer-events-none"/>
        <Input
          value={value ?? ""}
          onChange={onInput}
          onKeyDown={onKey}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder={placeholder || "Start typing an address…"}
          className="pl-8 pr-8 h-10 bg-white border-[#E5E0D8]"
          data-testid={testId}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#8A847A] animate-spin"/>}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-[#E5E0D8] rounded-md shadow-md max-h-64 overflow-y-auto" data-testid={`${testId}-suggestions`}>
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 border-[#F0EBE1] ${highlight === i ? "bg-[#F0EBE1]" : "hover:bg-[#FAF8F3]"}`}
              data-testid={`${testId}-suggestion-${i}`}
            >
              <div className="text-[#292723] leading-tight">{s.label}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
